import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart, formatBRL } from "@/context/CartContext";
import { CheckCircle2, ShoppingBag, Home } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  // Mercado Pago retorna ?ref=<external_reference>; Stripe retorna ?session_id=<id>
  const sessionId = params.get("session_id") || params.get("ref");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [poll, setPoll] = useState(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setError("Sessão inválida.");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/shop/checkout/status/${sessionId}`);
        if (cancelled) return;
        setOrder(data);
        if (data.payment_status === "paid") {
          clear();
          return;
        }
        if (attempts >= 12) return;
        setPoll(attempts);
        setTimeout(tick, 5000);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Não foi possível confirmar o pagamento.");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (error) {
    return (
      <div className="container-cuidar py-16 text-center">
        <h1 className="font-heading text-3xl font-extrabold">{error}</h1>
        <Link to="/produtos" className="mt-6 inline-block text-[hsl(var(--primary))] font-semibold hover:underline">Voltar para a loja</Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-cuidar py-16 text-center">
        <div className="animate-pulse">
          <div className="mx-auto h-14 w-14 rounded-full bg-[hsl(var(--muted))]" />
          <div className="mt-4 h-6 w-72 mx-auto bg-[hsl(var(--muted))] rounded-full" />
        </div>
      </div>
    );
  }

  const paid = order.payment_status === "paid";
  const pending = !paid;

  return (
    <div className="container-cuidar py-14 sm:py-20">
      <div className="max-w-2xl mx-auto text-center">
        <div className={`mx-auto grid place-items-center w-20 h-20 rounded-full ${paid ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold" data-testid="checkout-success-title">
          {paid ? "Obrigado! Pedido confirmado 💚" : "Estamos processando seu pagamento…"}
        </h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))]">
          {paid
            ? "Você acaba de ajudar dezenas de famílias. Enviamos um e-mail com os detalhes do pedido."
            : `Confirmação em andamento (tentativa ${poll + 1}). Não feche esta página.`}
        </p>

        <div className="mt-8 rounded-3xl border border-[hsl(var(--border))] bg-white p-6 text-left space-y-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Resumo do pedido</div>
          {(order.items || []).map((it, idx) => (
            <div key={idx} className="flex justify-between items-baseline text-sm border-b border-dashed border-[hsl(var(--border))] pb-2 last:border-b-0">
              <div>
                <div className="font-semibold">{it.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Tam. {it.size} · Qtd {it.qty}</div>
              </div>
              <div className="font-semibold text-[hsl(var(--primary))]">{formatBRL(it.unit_price_cents * it.qty)}</div>
            </div>
          ))}
          <div className="flex justify-between text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">Subtotal</span>
            <span className="font-semibold">{formatBRL(order.subtotal_cents)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">Frete</span>
            <span className="font-semibold">{order.shipping_cents === 0 ? "Grátis" : formatBRL(order.shipping_cents)}</span>
          </div>
          <div className="flex justify-between items-baseline border-t border-[hsl(var(--border))] pt-3">
            <span className="font-heading font-bold">Total</span>
            <span className="font-heading text-2xl font-extrabold text-[hsl(var(--primary))]">{formatBRL(order.total_cents)}</span>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/produtos" className="btn-pill inline-flex items-center gap-2 h-12 px-5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] font-semibold text-sm" data-testid="checkout-continue-btn">
            <ShoppingBag className="h-4 w-4" /> Continuar comprando
          </Link>
          <Link to="/" className="btn-pill inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 font-semibold text-sm">
            <Home className="h-4 w-4" /> Voltar ao início
          </Link>
        </div>

        {pending && (
          <p className="mt-6 text-xs text-[hsl(var(--muted-foreground))]">Estamos verificando o Stripe automaticamente. Se demorar, atualize a página em alguns instantes.</p>
        )}
      </div>
    </div>
  );
}
